import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { PriceListItemOptionModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/priceListItemOption/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/priceListItemOption/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/priceListItemOption/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/priceListItemOption/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/priceListItemOption/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/priceListItemOption/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/priceListItemOption/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/priceListItemOption/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/priceListItemOption/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/priceListItemOption/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/priceListItemOption/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/priceListItemOption/DELETE_FAILURE'

export type PriceListItemOptionModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PriceListItemOptionModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPriceListItemOptions(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().priceListItemOptions.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/price-list-item-options', options)
      .then(({ data }: { data: { data: PriceListItemOptionModel[] } }) => {
        const normalizedJson = normalize(data.data, Schemas.PRICE_LIST_ITEM_OPTION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data: data.data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPriceListItemOption(priceListItemOptionId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.priceListItemOptions.loadedIds]
    const loadedForKeys = [...state.priceListItemOptions.loadedForKeys]

    if (priceListItemOptionId && !loadedIds.includes(priceListItemOptionId)){
      loadedIds.push(priceListItemOptionId)
    }

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/price-list-item-options/${priceListItemOptionId}`, options)
      .then(({ data }: { data: PriceListItemOptionModel }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPriceListItemOption(
  priceListItemOption: Partial<PriceListItemOptionModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...priceListItemOption,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/price-list-item-options', options, config)
      .then(({ data }: { data: PriceListItemOptionModel }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePriceListItemOption(
  priceListItemOption: UpdateParams<PriceListItemOptionModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      ...priceListItemOption,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/price-list-item-options/${priceListItemOption.id}`, options, config)
      .then(({ data }: { data: PriceListItemOptionModel }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export type BulkCreateParams = {
  priceListItemId: number,
  productOptionIds: number[],
}

export function bulkCreatePriceListItemOptions(
  params: BulkCreateParams,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify(params),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/price-list-item-options/bulk', options, config)
      .then(({ data }: { data: PriceListItemOptionModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deletePriceListItemOption(priceListItemOption: DeleteParams<PriceListItemOptionModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/price-list-item-options/${priceListItemOption.id}`, {}, config)
      .then(({ status }) => {
        if (status !== 200) throw new Error()

        const deletedEntity = { id: priceListItemOption.id }
        const normalizedJson = normalize(deletedEntity, Schemas.PRICE_LIST_ITEM_OPTION)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data: deletedEntity }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function removePriceListItemOption(dispatch: AppDispatch, priceListItemOption: PriceListItemOptionModel) {
  const normalizedJson = normalize(priceListItemOption, Schemas.PRICE_LIST_ITEM_OPTION)
  dispatch(deleteEntity(normalizedJson))
}

// Reducer
export default function reducer(
  state: PriceListItemOptionModuleState = initialState,
  action: Action = { type: '' },
): PriceListItemOptionModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
